/**
 * DubRunChip.tsx — PLAT-11570
 *
 * The on-page trace of a dub generating in the background. `DubGenerateModal`
 * hands the request to `DubbingStore.startRun`, so closing the popover no
 * longer orphans it; this chip sits beside the language toggle in every host
 * and shows the run — estimated percent, language code — with a Cancel that
 * aborts it. Renders nothing while no run is in flight.
 *
 * The chrome, and the width discipline behind it, live in the shared `RunChip`
 * (PLAT-11690) it shares with `TranslationRunChip`. This file is the dub's own
 * half: which store to read, which story the run belongs to, and how its
 * progress is estimated.
 */
import { RunChip } from '@src/components/transcript/RunChip';
import { useDubbingStore } from '@src/stores-v2/StoreContext';
import { canonicalizeBcp47 } from '@src/utility/languageCaption';
import { observer } from 'mobx-react-lite';

import { useActiveDubRun } from './useActiveDubRun';
import { useDubGenerateProgress } from './useDubGenerateProgress';

export const DubRunChip = observer(function DubRunChip() {
  const dubbingStore = useDubbingStore();
  // Per-story, not per-workspace: without the guard, dubbing story A and
  // navigating to story B has B's header claim to be dubbing, and its ✕ cancel
  // A's run. The chip sits beside a language toggle that can be on any
  // language, so it reports whichever language is running — no narrowing here.
  const run = useActiveDubRun();
  const progress = useDubGenerateProgress(
    run !== null,
    run ?? { etaMs: 0, startedAt: 0 },
  );
  if (!run) return null;

  const percent = Math.round(progress);

  return (
    <RunChip
      percent={percent}
      code={canonicalizeBcp47(run.targetLanguage)}
      ariaLabel={`Dubbing ${run.languageCaption}, ${percent}%`}
      tooltip={`Dubbing ${run.languageCaption} — ${percent}%`}
      cancelLabel="Cancel dub"
      cancelTooltip="Cancel Dub"
      onCancel={() => dubbingStore.cancelRun()}
    />
  );
});
